import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "@/i18n";
import { Reveal, Overline } from "@/components/motion";
import MarqueeRibbon from "@/components/MarqueeRibbon";

const ABOUT_IMG = "https://images.unsplash.com/photo-1524069290683-0457abfe42c3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxob3BlZnVsJTIwY2hpbGRyZW4lMjBzY2hvb2x8ZW58MHx8fHwxNzg4MTY1NDY1fDA&ixlib=rb-4.1.0&q=85";

const About = () => {
  const { t } = useLanguage();
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 800], [0, 80]);
  const chapters = t("about.chapters");

  return (
    <div data-testid="about-page" className="pt-24">
      <section className="relative max-w-[1500px] mx-auto px-6 md:px-12 pt-16 pb-24 overflow-visible">
        <motion.div style={{ y: yImg }} className="absolute right-0 top-32 w-[34vw] max-w-[460px] hidden lg:block">
          <img src={ABOUT_IMG} alt="Children at school" data-testid="about-image" className="w-full aspect-[4/5] object-cover editorial-frame" />
          <p className="mt-4 text-xs text-sage italic">{t("about.imageCaption")}</p>
        </motion.div>
        <div className="max-w-2xl">
          <Reveal>
            <Overline>{t("about.overline")}</Overline>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-forest" data-testid="about-title">
              {t("about.title")}
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 text-base lg:text-lg leading-relaxed text-sage" data-testid="about-intro">{t("about.intro")}</p>
          </Reveal>
        </div>
      </section>

      <MarqueeRibbon />

      <section className="max-w-[1500px] mx-auto px-6 md:px-12 py-28" data-testid="manifesto-chapters">
        {chapters.map((ch, i) => (
          <Reveal key={ch.num} delay={0.05}>
            <div data-testid={`chapter-${ch.num}`} className={`relative grid grid-cols-1 lg:grid-cols-12 gap-8 py-16 border-t border-forest/15 ${i % 2 === 1 ? "lg:text-right" : ""}`}>
              <span className={`absolute -top-8 ${i % 2 === 1 ? "right-0" : "left-0"} font-serif text-[9rem] leading-none font-semibold text-forest/5 select-none pointer-events-none`}>
                {ch.num}
              </span>
              <div className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <h2 className="font-serif text-3xl md:text-4xl text-forest relative z-10">{ch.title}</h2>
              </div>
              <div className="lg:col-span-8">
                <p className="text-base lg:text-lg leading-relaxed text-sage max-w-2xl relative z-10 inline-block">{ch.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="bg-forest text-cream" data-testid="about-legal">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-24">
          <Reveal>
            <Overline light>{t("about.legalTitle")}</Overline>
            <p className="font-serif text-2xl md:text-3xl leading-relaxed max-w-3xl text-cream/90" data-testid="about-legal-text">{t("about.legalText")}</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
